import React from 'react';
import { MultiValueProps } from 'react-select';
import {
  BasicEntity,
  NamedFilterLabel,
  VersionedEntity,
} from '../../../EntityLabels';
import BuilderInfo from '../../../BuilderInfo';
import { useEntitySelectionContext } from '../context';
import { isNil, noop } from 'lodash';
import { ISelectedEntity } from '../types';
import { UnreachableCaseError } from 'ts-essentials';
import { createOptionalHandler, getLabelPropsForType } from '../utils';
import { BucketKind } from '../../../../utils/apollo/graphqlSchema';

const bucketKindCustomLabel: Record<BucketKind, string> = {
  [BucketKind.Enum]: 'Custom Value Binning',
  [BucketKind.Range]: 'Custom Range Binning',
  [BucketKind.Date]: 'Custom Date Binning',
};

/**
 * A component to display currently selected `selectedEntities` in the React Select box
 */
const EntitySelectionMultiValue = (
  props: MultiValueProps<ISelectedEntity, true, never>,
) => {
  const { data } = props;

  const {
    onRemoveEntity,
    labelProps: allLabelProps,
    onClose,
  } = useEntitySelectionContext();

  const onClear = !isNil(onRemoveEntity)
    ? () => onRemoveEntity(data)
    : undefined;

  if (data.__typename === 'VirtualEvent') {
    const labelProps = getLabelPropsForType(allLabelProps, 'VirtualEvent');
    return (
      <VersionedEntity
        label={data.name}
        onClear={onClear}
        isDisabled={data.isDisabled}
        isLatestVersion={data.isLatestVersion}
        onShowInfo={onClose}
      >
        <BuilderInfo
          kind="virtualEvent"
          virtualEvent={data}
          onExpand={labelProps?.onExpandFactory(data)}
        />
      </VersionedEntity>
    );
  } else if (data.__typename === 'NamedFilter') {
    const labelProps = getLabelPropsForType(allLabelProps, 'Filter');
    return (
      <NamedFilterLabel
        namedFilter={data}
        onRemoveNamedFilter={onClear ?? noop}
        onExpandNamedFilter={createOptionalHandler(labelProps, data)}
        onShowInfo={onClose}
      />
    );
  } else if (data.__typename === 'NamedBucketDef') {
    return (
      <VersionedEntity
        label={data.name}
        onClear={onClear}
        isDisabled={data.isDisabled}
        isLatestVersion={data.isLatestVersion}
        onShowInfo={onClose}
      >
        <BuilderInfo kind="namedBucketDef" namedBucketDef={data} />
      </VersionedEntity>
    );
  } else if (
    data.__typename === 'CompoundMeasure' ||
    data.__typename === 'SimpleMeasure'
  ) {
    const labelProps = getLabelPropsForType(allLabelProps, 'Measure');

    return (
      <VersionedEntity
        label={data.name}
        onClear={onClear}
        isDisabled={data.version?.isDisabled}
        isLatestVersion={data.version?.isLatestVersion}
        onShowInfo={onClose}
      >
        <BuilderInfo
          kind="namedMeasure"
          measure={data}
          onExpand={createOptionalHandler(labelProps, data)}
        />
      </VersionedEntity>
    );
  } else if (data.__typename === 'BasicEventValue') {
    return (
      <BasicEntity isDisabled={data.value.isDisabled} onClear={onClear}>
        {data.value.name}
      </BasicEntity>
    );
  } else if (
    data.__typename === 'CompoundCohort' ||
    data.__typename === 'ExpressionCohort' ||
    data.__typename === 'PerformedCohort' ||
    data.__typename === 'RetentionCohort' ||
    data.__typename === 'MeasureCohort' ||
    data.__typename === 'FunnelCohort'
  ) {
    const labelProps = getLabelPropsForType(allLabelProps, 'Cohort');

    return (
      <VersionedEntity
        label={data.name ?? ''}
        onClear={onClear}
        isDisabled={data.version?.isDisabled}
        isLatestVersion={data.version?.isLatestVersion}
        onShowInfo={onClose}
      >
        <BuilderInfo
          kind="namedCohort"
          cohort={data}
          fetched={true}
          onExpand={createOptionalHandler(labelProps, data)}
          isExpanded={labelProps?.isExpanded}
        />
      </VersionedEntity>
    );
  } else if (data.__typename === 'Field') {
    const labelProps = getLabelPropsForType(allLabelProps, 'Field');

    return (
      <BasicEntity
        onClear={onClear}
        variant={labelProps?.chipLabelVariantPicker(data)}
      >
        {data.name}
      </BasicEntity>
    );
  } else if (
    data.__typename === 'AnyEvent' ||
    data.__typename === 'AnyTableEvent'
  ) {
    return <BasicEntity onClear={onClear}>{data.name}</BasicEntity>;
  } else if (data.__typename === '__CustomBucketDef') {
    return (
      <BasicEntity onClear={onClear}>
        {bucketKindCustomLabel[data.kind]}
      </BasicEntity>
    );
  } else {
    throw new UnreachableCaseError(data);
  }
};
export default EntitySelectionMultiValue;
